import React, { useEffect, useContext, useCallback, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BusinessIcon from "@mui/icons-material/Business";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { AppContext } from "../../Contexts/AppContext";
import NavigationItems from "./NavigationItems/NavigationItems";
import PermissionaryApi from "../../api/permissionary";
import { Permissionary } from "../../models/Permissionary";
import { User, UserRole } from "../../models/User";
import UserApi from "../../api/user";
import {
	getUserPermissionariesIds,
	getUserRoleForPermissionary,
} from "../../utils/userPermissionaries";

const drawerWidth = 240;
const selectedPermissionaryStorageKey = "permissionaryId";
const legacySelectedPermissionaryStorageKey = "selectedPermissionaryId";

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
	open?: boolean;
}>(({ theme, open }) => ({
	flexGrow: 1,
	padding: theme.spacing(3),
	transition: theme.transitions.create("margin", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	marginLeft: `-${drawerWidth}px`,
	...(open && {
		transition: theme.transitions.create("margin", {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
		marginLeft: 0,
	}),
}));

interface AppBarProps extends MuiAppBarProps {
	open?: boolean;
}

const AppBar = styled(MuiAppBar, {
	shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
	transition: theme.transitions.create(["margin", "width"], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		width: `calc(100% - ${drawerWidth}px)`,
		marginLeft: `${drawerWidth}px`,
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	padding: theme.spacing(0, 1),
	// necessary for content to be below app bar
	...theme.mixins.toolbar,
	justifyContent: "flex-end",
}));

const roleLabels: Record<UserRole, string> = {
	[UserRole.GOD]: "Super administrador",
	[UserRole.ADMIN]: "Administrador",
	[UserRole.USER]: "Usuario",
};

export default function NavigationBar(props: { children: React.ReactNode }) {
	const { children } = props;
	const theme = useTheme();
	const [open, setOpen] = React.useState(true);
	const [permissionaries, setPermissionaries] = React.useState<Permissionary[]>(
		[]
	);
	const [user, setUser] = useState<User | null>(null);

	const { title, userContext, setUserContext } = useContext(AppContext);

	const handleDrawerOpen = () => {
		setOpen(true);
	};

	const handleDrawerClose = () => {
		setOpen(false);
	};

	const getPermissionaryId = useCallback((permissionary: Permissionary) => {
		return permissionary.id || permissionary._id;
	}, []);

	const fetchPermissionaries = useCallback(async () => {
		if (!userContext?.userId) return;

		try {
			const response = (await PermissionaryApi.getPermissionariesByUserId({
				userId: userContext.userId,
				token: localStorage.getItem("accessToken"),
			})) as Permissionary[];
			const userPermissionaries = Array.isArray(response) ? response : [];
			const allowedPermissionaryIds =
				userPermissionaries.length > 0
					? userPermissionaries.map(getPermissionaryId)
					: getUserPermissionariesIds(userContext);
			const storedPermissionaryId =
				localStorage.getItem(selectedPermissionaryStorageKey) ||
				localStorage.getItem(legacySelectedPermissionaryStorageKey);
			const selectedPermissionaryId =
				storedPermissionaryId &&
				allowedPermissionaryIds.includes(storedPermissionaryId)
					? storedPermissionaryId
					: allowedPermissionaryIds[0] || "";
			const selectedPermissionary = userPermissionaries.find(
				(permissionary) => getPermissionaryId(permissionary) === selectedPermissionaryId
			);
			const selectedPermissionaryName = selectedPermissionary?.name || "";

			setPermissionaries(userPermissionaries);
			if (selectedPermissionaryId) {
				localStorage.setItem(
					selectedPermissionaryStorageKey,
					selectedPermissionaryId
				);
				localStorage.removeItem(legacySelectedPermissionaryStorageKey);
			} else {
				localStorage.removeItem(selectedPermissionaryStorageKey);
				localStorage.removeItem(legacySelectedPermissionaryStorageKey);
			}

			if (
				selectedPermissionaryId !== userContext.permissionaryId ||
				selectedPermissionaryName !== userContext.permissionaryName ||
				getUserRoleForPermissionary(userContext, selectedPermissionaryId) !==
					userContext.role ||
				allowedPermissionaryIds.join("|") !==
					getUserPermissionariesIds(userContext).join("|")
			) {
				setUserContext({
					...userContext,
					permissionariesIds: allowedPermissionaryIds,
					permissionaryId: selectedPermissionaryId,
					permissionaryName: selectedPermissionaryName,
					role: getUserRoleForPermissionary(userContext, selectedPermissionaryId),
				});
			}
		} catch (error) {
			setPermissionaries([]);
		}
	}, [getPermissionaryId, setUserContext, userContext]);

	const fetchUser = useCallback(async () => {
		try {
			const response = (await UserApi.getUserById({
				token: localStorage.getItem("accessToken"),
				userId: userContext?.userId || "",
			})) as User;

			setUser(response);
		} catch (error) {
			console.log(error);
			setUser(null);
		}
	}, [userContext]);

	useEffect(() => {
		fetchPermissionaries();
	}, [fetchPermissionaries]);

	useEffect(() => {
		fetchUser();
	}, [fetchUser]);

	const handlePermissionaryChange = useCallback(
		(event: SelectChangeEvent) => {
			const permissionaryId = event.target.value;
			const selectedPermissionary = permissionaries.find(
				(permissionary) => getPermissionaryId(permissionary) === permissionaryId
			);

			localStorage.setItem(selectedPermissionaryStorageKey, permissionaryId);
			localStorage.removeItem(legacySelectedPermissionaryStorageKey);
			setUserContext(
				userContext
					? {
							...userContext,
							permissionaryId,
							permissionaryName: selectedPermissionary?.name || "",
							role: getUserRoleForPermissionary(userContext, permissionaryId),
					  }
					: null
			);
		},
		[getPermissionaryId, permissionaries, setUserContext, userContext]
	);

	return (
		<Box sx={{ display: "flex" }}>
			<CssBaseline />
			<AppBar position='fixed' open={open}>
				<Toolbar>
					<IconButton
						color='inherit'
						aria-label='open drawer'
						onClick={handleDrawerOpen}
						edge='start'
						sx={{ mr: 2, ...(open && { display: "none" }) }}>
						<MenuIcon />
					</IconButton>
					<Typography variant='h6' noWrap component='div'>
						{title}
					</Typography>
				</Toolbar>
			</AppBar>
			<Drawer
				sx={{
					width: drawerWidth,
					flexShrink: 0,
					"& .MuiDrawer-paper": {
						width: drawerWidth,
						boxSizing: "border-box",
						display: "flex",
						background:
							"linear-gradient(180deg, #0759ad 0%, #146fcb 48%, #1976d2 100%)",
						borderRight: 0,
					},
				}}
				variant='persistent'
				anchor='left'
				open={open}>
				<DrawerHeader
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						gap: 1,
						py: 1.5,
					}}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<FormControl
							size='small'
							fullWidth
							sx={{
								mt: 0.25,
								"& .MuiInputLabel-root": {
									color: "rgba(255,255,255,0.72)",
									fontSize: 12,
									fontWeight: 700,
									letterSpacing: "0.02em",
								},
								"& .MuiInputLabel-root.Mui-focused": {
									color: "rgba(255,255,255,0.88)",
								},
								"& .MuiOutlinedInput-root": {
									color: "white",
									borderRadius: 2,
									backgroundColor: "rgba(255,255,255,0.12)",
									boxShadow:
										"inset 0 1px 0 rgba(255,255,255,0.2), 0 10px 24px rgba(0,0,0,0.12)",
									"& fieldset": { borderColor: "rgba(255,255,255,0.34)" },
									"&:hover fieldset": {
										borderColor: "rgba(255,255,255,0.7)",
									},
									"&.Mui-focused fieldset": {
										borderColor: "rgba(255,255,255,0.9)",
										borderWidth: 1,
									},
								},
								"& .MuiSelect-select": {
									display: "flex",
									alignItems: "center",
									gap: 1,
									py: 1.05,
								},
								"& .MuiSvgIcon-root": { color: "white" },
							}}>
							<InputLabel id='permissionary-select-label'>
								Permisionario
							</InputLabel>
							<Select
								labelId='permissionary-select-label'
								id='permissionary-select'
								value={userContext?.permissionaryId || ""}
								label='Permisionario'
								onChange={handlePermissionaryChange}
								disabled={permissionaries.length === 0}
								renderValue={(selected) => {
									const selectedPermissionary = permissionaries.find(
										(permissionary) =>
											getPermissionaryId(permissionary) === selected,
									);

									return (
										<Box
											component='span'
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1,
												minWidth: 0,
											}}>
											<BusinessIcon sx={{ fontSize: 18, flexShrink: 0 }} />
											<Box
												component='span'
												sx={{
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													fontWeight: 700,
												}}>
												{selectedPermissionary?.name || "Permisionario"}
											</Box>
										</Box>
									);
								}}
								MenuProps={{
									PaperProps: {
										sx: {
											mt: 0.75,
											borderRadius: 2,
											boxShadow: "0 18px 48px rgba(15, 23, 42, 0.18)",
											"& .MuiMenuItem-root": {
												mx: 0.75,
												my: 0.35,
												borderRadius: 1.5,
												fontWeight: 600,
												"&.Mui-selected": {
													backgroundColor: "rgba(25, 118, 210, 0.12)",
													color: "#0759ad",
												},
												"&.Mui-selected:hover": {
													backgroundColor: "rgba(25, 118, 210, 0.18)",
												},
											},
										},
									},
								}}>
								{permissionaries.map((currentPermissionary) => {
									const currentPermissionaryId =
										getPermissionaryId(currentPermissionary);

									return (
										<MenuItem
											key={currentPermissionaryId}
											value={currentPermissionaryId}>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1.25,
													minWidth: 0,
												}}>
												<BusinessIcon
													sx={{ color: "#1976d2", fontSize: 19, flexShrink: 0 }}
												/>
												<Typography
													variant='body2'
													sx={{
														fontWeight: 700,
														overflow: "hidden",
														textOverflow: "ellipsis",
													}}>
													{currentPermissionary.name}
												</Typography>
											</Box>
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>
					</Box>
					<IconButton onClick={handleDrawerClose}>
						{theme.direction === "ltr" ? (
							<ChevronLeftIcon sx={{ color: "white" }} />
						) : (
							<ChevronRightIcon sx={{ color: "white" }} />
						)}
					</IconButton>
				</DrawerHeader>
				<Divider />
				<NavigationItems handleClose={() => handleDrawerClose()} />
				{user ? (
					<Box
						sx={{
							mt: "auto",
							p: 2,
							borderTop: "1px solid rgba(255,255,255,0.16)",
						}}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1.25,
								borderRadius: 2,
								backgroundColor: "rgba(255,255,255,0.12)",
								p: 1.25,
							}}>
							<AccountCircleIcon sx={{ color: "white", fontSize: 34 }} />
							<Box sx={{ minWidth: 0 }}>
								<Typography
									variant='caption'
									sx={{
										color: "rgba(255,255,255,0.72)",
										display: "block",
										fontWeight: 700,
										lineHeight: 1.2,
									}}>
									{roleLabels[userContext?.role || user.role] ||
										userContext?.role ||
										user.role}
								</Typography>
								<Typography
									id='menu-title'
									variant='body2'
									sx={{
										color: "white",
										fontWeight: 600,
										lineHeight: 1.35,
										overflowWrap: "anywhere",
									}}>
									{user.email}
								</Typography>
							</Box>
						</Box>
					</Box>
				) : null}
			</Drawer>
			<Main open={open} key={userContext?.permissionaryId || "no-permissionary"}>
				<DrawerHeader />
				{children}
			</Main>
		</Box>
	);
}
